import React, { useState } from 'react';
import { ArrowLeft, Send, Loader2, Mail, User, MessageSquare, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ContactPage = ({ onBack }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, { name, email, subject, message });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al enviar el mensaje. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      <header className="border-b border-gray-800 bg-[#0d0d0f]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            data-testid="contact-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>
          <img src="/logo.png" alt="TraceMetrics" className="h-10 w-auto" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Contacto</h1>
          <p className="text-gray-400">
            ¿Tienes alguna pregunta, sugerencia o necesitas ayuda? Escríbenos y te responderemos lo antes posible.
          </p>
        </div>

        {sent ? (
          <div className="bg-[#0d0d0f] border border-emerald-500/30 rounded-2xl p-10 text-center" data-testid="contact-success">
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-3">Mensaje enviado</h2>
            <p className="text-gray-400 leading-relaxed mb-6">
              Hemos recibido tu mensaje correctamente. Te hemos enviado una confirmación a tu correo electrónico. Nuestro equipo revisará tu consulta y te responderá en breve.
            </p>
            <button
              onClick={onBack}
              className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-lg font-semibold hover:from-cyan-400 hover:to-teal-400 transition-all"
              data-testid="contact-back-to-home"
            >
              Volver al inicio
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#0d0d0f] border border-gray-800 rounded-2xl p-8 space-y-5" data-testid="contact-form">
            {error && (
              <div className="p-3 rounded-lg text-sm bg-red-500/10 text-red-400 border border-red-500/20">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm text-gray-400 mb-2">Nombre</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-[#0a0a0b] border border-gray-800 rounded-lg focus:outline-none focus:border-cyan-500 transition-colors"
                  placeholder="Tu nombre"
                  required
                  data-testid="contact-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-[#0a0a0b] border border-gray-800 rounded-lg focus:outline-none focus:border-cyan-500 transition-colors"
                  placeholder="tu@email.com"
                  required
                  data-testid="contact-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Asunto</label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-[#0a0a0b] border border-gray-800 rounded-lg focus:outline-none focus:border-cyan-500 transition-colors appearance-none"
                  required
                  data-testid="contact-subject-select"
                >
                  <option value="">Selecciona un asunto</option>
                  <option value="soporte">Soporte técnico</option>
                  <option value="facturacion">Facturación y pagos</option>
                  <option value="exchange">Solicitar nuevo exchange</option>
                  <option value="cuenta">Mi cuenta</option>
                  <option value="sugerencia">Sugerencia o mejora</option>
                  <option value="privacidad">Privacidad y datos personales</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Mensaje</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full px-4 py-3 bg-[#0a0a0b] border border-gray-800 rounded-lg focus:outline-none focus:border-cyan-500 transition-colors resize-none"
                placeholder="Describe tu consulta con el mayor detalle posible..."
                rows={5}
                required
                minLength={10}
                data-testid="contact-message-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-lg font-semibold hover:from-cyan-400 hover:to-teal-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="contact-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Enviar mensaje
                </>
              )}
            </button>

            <p className="text-gray-600 text-xs text-center">
              Al enviar este formulario aceptas nuestra Política de Privacidad.
              Tu mensaje será procesado por nuestro equipo de soporte.
            </p>
          </form>
        )}
      </main>

      <footer className="border-t border-gray-800 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} TraceMetrics · Noakai LLC. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
